/**
 * 资源管理 API
 */
import type { Asset } from '@/types/editor';
import type { MediaDelivery } from '@/lib/media-delivery';
import type {
  ApiResponse,
  PresignUploadResponse,
  ConfirmUploadResponse,
  WaveformData,
  ProcessAdditionsStatus,
} from './types';
import { ApiClient } from './client';
import { getAssetStreamUrl } from './media-proxy';
import { translateClient } from '@/lib/i18n/client';
import { getAuthSessionVersion } from '@/stores/authStore';

// ==================== 调试开关 ====================
const DEBUG_UPLOAD = false;  // 上传日志开关
const uploadLog = (...args: unknown[]) => { if (DEBUG_UPLOAD) console.log(...args); };

const MAX_VIDEO_UPLOAD_MB = 200;
const MAX_VIDEO_UPLOAD_BYTES = MAX_VIDEO_UPLOAD_MB * 1024 * 1024;

// 大文件阈值：超过此大小使用分片上传 (50MB)
const CHUNK_UPLOAD_THRESHOLD = 50 * 1024 * 1024;

// 进度日志节流间隔（毫秒）
const PROGRESS_LOG_THROTTLE = 500;

export interface AssetAccessUrl {
  url: string;
  filename: string;
  mime_type?: string | null;
  expires_in: number;
  media_urls?: { stream: string; proxy: string; thumbnail: string; hls: string };
}

class AssetApi extends ApiClient {
  /** Batch permission checks once; media bytes use the returned direct URLs. */
  async getDelivery(assetIds: string[], signal?: AbortSignal): Promise<ApiResponse<{ items: MediaDelivery[] }>> {
    return this.request('/assets/delivery', {
      method: 'POST',
      body: JSON.stringify({ asset_ids: assetIds }),
      signal,
    });
  }

  /**
   * 获取资源列表
   */
  async getAssets(params?: {
    project_id?: string;
    type?: string;
    limit?: number;
  }): Promise<ApiResponse<{ items: Asset[] }>> {
    const searchParams = new URLSearchParams();
    if (params?.project_id) searchParams.set('project_id', params.project_id);
    if (params?.type) searchParams.set('type', params.type);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    
    const query = searchParams.toString();
    return this.request(`/assets${query ? `?${query}` : ''}`);
  }

  /**
   * 获取预签名上传 URL
   */
  async presignUpload(data: {
    project_id: string;
    file_name: string;
    file_size: number;
    content_type: string;
  }): Promise<ApiResponse<PresignUploadResponse>> {
    return this.request('/assets/presign-upload', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * 确认上传完成
   */
  async confirmUpload(data: {
    asset_id: string;
    project_id: string;
    file_name: string;
    file_size: number;
    content_type: string;
    storage_path: string;
    duration?: number;  // 毫秒
  }): Promise<ApiResponse<ConfirmUploadResponse>> {
    return this.request('/assets/confirm-upload', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * 获取资源详情
   */
  async getAsset(assetId: string): Promise<ApiResponse<Asset>> {
    return this.request(`/assets/${assetId}`);
  }

  async getAccessUrl(
    assetId: string,
    disposition: 'inline' | 'attachment' = 'inline',
  ): Promise<ApiResponse<AssetAccessUrl>> {
    return this.request(`/assets/${assetId}/access-url?disposition=${disposition}`);
  }

  /**
   * 删除资源
   */
  async deleteAsset(assetId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request(`/assets/${assetId}`, {
      method: 'DELETE',
    });
  }

  /**
   * ★ 为 AI 生成任务预创建占位 asset 记录
   * 返回真实 asset_id，用于创建 placeholder FreeNode 时避免 FK 约束报错
   */
  async createPlaceholderAsset(data: {
    project_id: string;
    file_type: 'video' | 'image' | '3d_model';
    name?: string;
  }): Promise<ApiResponse<{ asset_id: string }>> {
    return this.request('/assets/create-placeholder', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * ★ AI 生成完成后，将占位 asset 标记为 ready 并写入真实 URL
   */
  async finalizePlaceholderAsset(assetId: string, data: {
    result_url: string;
    output_asset_id?: string;
  }): Promise<ApiResponse<unknown>> {
    return this.request(`/assets/${assetId}/finalize-placeholder`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * 获取波形数据
   */
  async getWaveform(assetId: string): Promise<ApiResponse<WaveformData>> {
    return this.request(`/assets/${assetId}/waveform`);
  }

  /**
   * 处理编辑器内添加的新素材
   * 会执行 ASR 转写并将 clips 追加到时间轴末尾
   */
  async processAdditions(data: {
    project_id: string;
    asset_ids: string[];
    enable_asr?: boolean;
    enable_smart_camera?: boolean;
    idempotency_key?: string;
  }): Promise<ApiResponse<{ task_id: string; status: string }>> {
    const response = await this.request<{ task_id: string; status: string }>('/assets/process-additions', {
      method: 'POST',
      headers: { 'Idempotency-Key': data.idempotency_key || generateAssetId() },
      body: JSON.stringify({
        project_id: data.project_id,
        asset_ids: data.asset_ids,
        enable_asr: data.enable_asr ?? true,
        enable_smart_camera: data.enable_smart_camera ?? false,
      }),
    });
    if (response.error) {
      const keys: Record<string, string> = {
        Unauthorized: 'unauthorized', Forbidden: 'forbidden', 'Asset not found': 'assetNotFound',
        'Invalid asset IDs': 'invalidAssetIds', 'Invalid idempotency key': 'invalidIdempotencyKey',
        Conflict: 'conflict', 'Task not found': 'taskNotFound',
      };
      const key = keys[response.error.message];
      if (key) response.error.message = translateClient('mediaAccess', key);
    }
    return response;
  }

  /**
   * 获取素材处理任务状态
   */
  async getProcessAdditionsStatus(taskId: string): Promise<ApiResponse<ProcessAdditionsStatus>> {
    return this.request(`/assets/process-additions/${taskId}`);
  }
}

export const assetApi = new AssetApi();

const assetAccessUrlCache = new Map<string, { value: AssetAccessUrl; expiresAt: number }>();
const assetAccessUrlInflight = new Map<string, Promise<AssetAccessUrl>>();
let assetAccessSessionVersion = getAuthSessionVersion();

/** 同一资源的并发 URL 请求复用一个 Promise；失败后立即驱逐，允许用户重试。 */
export async function getCachedAssetAccessUrl(
  assetId: string,
  disposition: 'inline' | 'attachment' = 'inline',
): Promise<AssetAccessUrl> {
  const sessionVersion = getAuthSessionVersion();
  if (assetAccessSessionVersion !== sessionVersion) {
    assetAccessUrlCache.clear();
    assetAccessUrlInflight.clear();
    assetAccessSessionVersion = sessionVersion;
  }
  const cacheKey = `${sessionVersion}:${assetId}:${disposition}`;
  const cached = assetAccessUrlCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.value;
  }

  const pending = assetAccessUrlInflight.get(cacheKey);
  if (pending) return pending;

  const request = assetApi.getAccessUrl(assetId, disposition).then((response) => {
    if (getAuthSessionVersion() !== sessionVersion) {
      throw new Error(translateClient('mediaAccess', 'unauthorized'));
    }
    if (!response.success || response.error || !response.data?.url) {
      throw new Error(response.error?.message || translateClient('mediaAccess', 'forbidden'));
    }
    const ttlMs = Math.max(0, response.data.expires_in * 1000 - 30_000);
    assetAccessUrlCache.set(cacheKey, {
      value: response.data,
      expiresAt: Date.now() + ttlMs,
    });
    return response.data;
  }).finally(() => {
    assetAccessUrlInflight.delete(cacheKey);
  });

  assetAccessUrlInflight.set(cacheKey, request);
  return request;
}

/**
 * 上传进度回调
 */
type UploadProgressCallback = (progress: {
  bytesUploaded: number;
  bytesTotal: number;
  percentage: number;
}) => void;

/**
 * 生成唯一的 asset ID
 */
function generateAssetId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * 使用预签名 URL 上传文件（COS 兼容）
 */
async function uploadWithPresignedUrl(
  file: File,
  uploadUrl: string,
  onProgress?: UploadProgressCallback
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentage = Math.round((event.loaded / event.total) * 100);
        onProgress?.({
          bytesUploaded: event.loaded,
          bytesTotal: event.total,
          percentage,
        });
      }
    };
    
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(translateClient('workflow', 'uploadFailedSafe')));
      }
    };
    
    xhr.onerror = () => reject(new Error(translateClient('workflow', 'uploadFailedSafe')));
    xhr.ontimeout = () => reject(new Error(translateClient('workflow', 'uploadFailedSafe')));
    
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
}

/**
 * 便捷函数：上传视频文件
 * 
 * 使用 COS 预签名 URL 上传
 * 
 * @param file 视频文件
 * @param projectId 项目ID
 * @param duration 视频时长（毫秒），前端本地提取
 * @param onProgress 上传进度回调
 */
export async function uploadVideo(
  file: File,
  projectId?: string,
  duration?: number,
  onProgress?: UploadProgressCallback
): Promise<{
  asset_id: string;
  url: string;
}> {
  // 前端文件类型 & 大小校验
  const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
  if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
    throw new Error(translateClient('workflow', 'videoUploadUnsupportedFormat'));
  }
  if (file.size > MAX_VIDEO_UPLOAD_BYTES) {
    throw new Error(translateClient('workflow', 'videoUploadTooLarge', { size: (file.size / 1024 / 1024).toFixed(1), limit: MAX_VIDEO_UPLOAD_MB }));
  }

  const api = new AssetApi();
  const pid = projectId || 'temp-project';
  
  uploadLog('[Upload] 使用预签名 URL 上传');
  
  // 1. 先创建 asset 记录获取 asset_id 和上传路径
  const presignResult = await api.presignUpload({
    project_id: pid,
    file_name: file.name,
    file_size: file.size,
    content_type: file.type,
  });

  if (presignResult.error || !presignResult.data) {
    throw presignResult.error ?? new Error(translateClient('workflow', 'uploadFailedSafe'));
  }

  const { asset_id, upload_url, storage_path } = presignResult.data;

  // 校验预签名 URL 必须为 HTTPS 且指向合法存储域名
  try {
    const parsed = new URL(upload_url);
    if (parsed.protocol !== 'https:' || !parsed.hostname.endsWith('.myqcloud.com')) {
      throw new Error(translateClient('workflow', 'uploadFailedSafe'));
    }
  } catch {
    throw new Error(translateClient('workflow', 'uploadFailedSafe'));
  }

  // 2. 使用预签名 URL 上传到 COS
  await uploadWithPresignedUrl(file, upload_url, onProgress);

  // 3. 确认上传完成（更新 asset 记录）
  const confirmResult = await api.confirmUpload({
    asset_id,
    project_id: pid,
    file_name: file.name,
    file_size: file.size,
    content_type: file.type,
    storage_path,
    duration,
  });

  if (confirmResult.error || !confirmResult.data) {
    throw confirmResult.error ?? new Error(translateClient('workflow', 'uploadFailedSafe'));
  }

  return {
    asset_id: confirmResult.data.id,
    url: getAssetStreamUrl(confirmResult.data.id),
  };
}
